// eslint-disable-next-line simple-import-sort/imports
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from 'react-query';
import { Router } from 'react-router-dom';
import { OryAuth } from '@distribusion/portal-lib/auth';
import { DEFAULT_COLOR_SCHEME } from '@distribusion/portal-lib/constants';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '@distribusion/portal-lib/constants';
import i18n from '@distribusion/portal-lib/i18n';
import { queryClient } from '@distribusion/portal-lib/query-client';
import { COMMON_NAMESPACE } from '@distribusion/portal-lib/translation';
import ConfigProvider from 'antd/es/config-provider';
import * as Sentry from '@sentry/react';
import { ROUTES } from 'constants/common';
import { createBrowserHistory } from 'history';
import { domainConfig } from 'lib/axios';
import { handleBeforeSend } from 'utils/sentry';
import { NAMESPACE as ROOT_NAMESPACE } from 'lib/translation';
import { featureFlags } from 'lib/feature-flags';

import config from './config';

import '@distribusion/portal-styleguide/less/index.less';
import '@distribusion/portal-lib/components.css';

__webpack_public_path__ = config.publicPath;

// It is important for styles appliance order
// to import App after global and components styles.
import App from './app';

if (featureFlags.mockRequests) {
  import('./mocks/browser').then(({ worker }) => {
    worker.start({ onUnhandledRequest: 'bypass' });
  });
}

ConfigProvider.config({
  theme: {
    primaryColor: DEFAULT_COLOR_SCHEME.PRIMARY_COLOR,
  },
});

i18n.init({
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  debug: config.stage === 'development',
  // load all namespaces on init to avoid additional translations requests
  // cause it returns all namespaces every time
  ns: [
    'agency-management',
    'booking-management',
    'common',
    'configuration',
    'dashboards',
    'finances',
    'pricing',
    COMMON_NAMESPACE,
    ROOT_NAMESPACE,
  ],
  translationsApiUrl: config.distribusion.whitelabelTranslationsApiUrl + '/api/translations/portal',
});

OryAuth.init({
  basePath: domainConfig.oryUrl,
  baseOptions: {
    params: {
      // Means that we using getSession to get and refresh jwt token
      tokenize_as: config.stage === 'production' ? 'jwt_distribusion' : 'jwt_distribusion_test',
    },
    loginUrl: !featureFlags.newOryUI ? ROUTES.LOGIN : undefined,
  },
});

const history = createBrowserHistory();

Sentry.init({
  dsn: config.sentry.dsn,
  environment: config.stage,
  // Commented as non correct because we use global instance as bus for different releases
  // TODO: check is logging become worse without it
  // release: config.sentry.release,
  integrations: [
    Sentry.reactRouterV5BrowserTracingIntegration({ history, tracePropagationTargets: [] }),
    Sentry.moduleMetadataIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: config.stage === 'production' ? 0.4 : 0,
  normalizeDepth: 5,
  beforeSend: handleBeforeSend,
});

const rootElement = document.getElementById('app');

if (rootElement) {
  createRoot(rootElement).render(
    <QueryClientProvider client={queryClient}>
      <Router history={history}>
        <App />
      </Router>
    </QueryClientProvider>,
  );
}
